<!-- eslint-disable vue/no-v-html -->
<template>
    <div
        class="session-files-artifact"
        :class="{ inline: isInline }">
        <div
            v-if="!selectedFile"
            class="session-files-artifact__list">
            <div
                v-for="file in files"
                :key="file.path"
                class="session-files-artifact__file"
                @click="handleFileClick(file)">
                <pendo-icon
                    :type="getFileTypeIcon(file)"
                    size="16" />
                <div class="session-files-artifact__file-info">
                    <span class="session-files-artifact__file-name">{{ file.name }}</span>
                    <span class="session-files-artifact__file-size">{{ formatFileSize(file.size) }}</span>
                </div>
                <pendo-loading-indicator
                    v-if="downloadingFiles[file.path]"
                    size="small"
                    class="session-files-artifact__file-action" />
                <pendo-icon
                    v-else-if="isPreviewable(file)"
                    type="eye"
                    size="14"
                    class="session-files-artifact__file-action" />
                <pendo-icon
                    v-else
                    type="download"
                    size="14"
                    class="session-files-artifact__file-action" />
            </div>
            <div
                v-if="files.length === 0"
                class="session-files-artifact__empty">
                No files were created during this session.
            </div>
        </div>
        <div
            v-else
            class="session-files-artifact__preview">
            <div class="session-files-artifact__preview-header">
                <button
                    class="session-files-artifact__back-button"
                    @click="selectedFile = null">
                    <pendo-icon
                        type="arrow-left"
                        size="14" />
                    Back to files
                </button>
                <span class="session-files-artifact__preview-filename">{{ selectedFile.name }}</span>
                <div
                    v-if="isHtmlFile(selectedFile)"
                    class="session-files-artifact__render-toggle">
                    <span :class="{ active: !showHtmlRender }">Code</span>
                    <pendo-toggle
                        :value="showHtmlRender"
                        @change="showHtmlRender = $event" />
                    <span :class="{ active: showHtmlRender }">Preview</span>
                </div>
                <button
                    class="session-files-artifact__download-button"
                    :disabled="downloadingFiles[selectedFile.path]"
                    @click="handleDownload(selectedFile)">
                    <pendo-loading-indicator
                        v-if="downloadingFiles[selectedFile.path]"
                        size="small" />
                    <pendo-icon
                        v-else
                        type="download"
                        size="14" />
                </button>
            </div>
            <div
                v-if="previewLoading"
                class="session-files-artifact__preview-loading">
                Loading file contents...
            </div>
            <div
                v-else-if="previewError"
                class="session-files-artifact__preview-error">
                {{ previewError }}
            </div>
            <div
                v-else-if="previewTruncated"
                class="session-files-artifact__preview-content">
                <highlightjs
                    v-if="highlightLanguage"
                    :language="highlightLanguage"
                    :code="previewContent" />
                <pre v-else><code>{{ previewContent }}</code></pre>
                <div class="session-files-artifact__truncated-notice">
                    File truncated at 500KB.
                    <button @click="handleDownload(selectedFile)">
                        Download full file
                    </button>
                </div>
            </div>
            <div
                v-else-if="showHtmlRender && isHtmlFile(selectedFile)"
                class="session-files-artifact__preview-render">
                <iframe
                    :srcdoc="previewContent"
                    sandbox="allow-scripts"
                    class="session-files-artifact__html-iframe" />
            </div>
            <div
                v-else
                class="session-files-artifact__preview-content">
                <div
                    v-if="isMarkdownFile(selectedFile)"
                    class="rendered-markdown"
                    v-html="renderedMarkdown" />
                <highlightjs
                    v-else-if="highlightLanguage"
                    :language="highlightLanguage"
                    :code="previewContent" />
                <pre v-else><code>{{ previewContent }}</code></pre>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoIcon, PendoLoadingIndicator, PendoToggle } from '@pendo/components';
import { mapActions } from 'vuex';
import { renderMarkdown } from '@/common/marked/markdown.utils.js';
import { fetchClaudeAgentFile } from '@/vuex/modules/ask-pendo/ask-pendo.utils';
import { getLanguageForExtension } from './syntax-highlight.utils';

const MAX_PREVIEW_SIZE = 500 * 1024; // 500KB

const PREVIEWABLE_EXTENSIONS = new Set([
    '.py', '.js', '.ts', '.jsx', '.tsx',
    '.css', '.less', '.scss',
    '.json', '.yaml', '.yml', '.toml',
    '.csv', '.tsv',
    '.md', '.txt', '.log',
    '.html', '.xml', '.svg',
    '.sh', '.bash', '.zsh',
    '.sql', '.r', '.go', '.rs', '.java',
]);

export default {
    name: 'SessionFilesArtifact',
    components: {
        PendoIcon,
        PendoLoadingIndicator,
        PendoToggle,
    },
    props: {
        artifactId: {
            type: String,
            default: null,
        },
        data: {
            type: Object,
            default: null,
        },
        isInline: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            selectedFile: null,
            previewContent: '',
            previewLoading: false,
            previewError: null,
            previewTruncated: false,
            downloadingFiles: {},
            showHtmlRender: false,
        };
    },
    computed: {
        files () {
            return this.data?.files || [];
        },
        conversationId () {
            return this.data?.conversation_id || '';
        },
        renderedMarkdown () {
            if (!this.previewContent) return '';

            return renderMarkdown(this.previewContent);
        },
        highlightLanguage () {
            if (!this.selectedFile) return null;

            return getLanguageForExtension(this.getExtension(this.selectedFile.name));
        },
    },
    beforeUnmount () {
        this.clearArtifactMenuItems(this.artifactId);
    },
    mounted () {
        this.setArtifactMenuItems({
            artifactId: this.artifactId,
            items: [
                {
                    id: 'download-all',
                    icon: 'download',
                    label: 'Download all files',
                    onClick: () => {
                        this.files.forEach((file) => this.handleDownload(file));
                    },
                },
            ],
        });
    },
    methods: {
        ...mapActions('askPendo', ['clearArtifactMenuItems', 'setArtifactMenuItems']),
        getFileTypeIcon (file) {
            const ext = this.getExtension(file.name);

            switch (ext) {
                case '.py':
                case '.js':
                case '.ts':
                case '.jsx':
                case '.tsx':
                case '.go':
                case '.rs':
                case '.java':
                case '.r':
                case '.sh':
                case '.bash':
                case '.sql':
                    return 'file-code';
                case '.csv':
                case '.tsv':
                    return 'table-2';
                case '.json':
                    return 'braces';
                case '.md':
                case '.txt':
                case '.log':
                    return 'file-text';
                case '.html':
                case '.xml':
                case '.svg':
                    return 'code';
                default:
                    return 'file';
            }
        },
        getExtension (filename) {
            const lastDot = filename.lastIndexOf('.');
            if (lastDot === -1) return '';

            return filename.slice(lastDot).toLowerCase();
        },
        isPreviewable (file) {
            const ext = this.getExtension(file.name);

            return PREVIEWABLE_EXTENSIONS.has(ext);
        },
        isMarkdownFile (file) {
            return this.getExtension(file.name) === '.md';
        },
        isHtmlFile (file) {
            return this.getExtension(file.name) === '.html';
        },
        formatFileSize (bytes) {
            if (bytes == null) return '';
            if (bytes < 1024) return `${bytes} B`;
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        },
        async handleFileClick (file) {
            if (this.isPreviewable(file)) {
                await this.loadPreview(file);
            } else {
                this.handleDownload(file);
            }
        },
        async loadPreview (file) {
            this.selectedFile = file;
            this.previewLoading = true;
            this.previewError = null;
            this.previewContent = '';
            this.previewTruncated = false;
            this.showHtmlRender = this.isHtmlFile(file);

            try {
                const { data } = await fetchClaudeAgentFile(this.conversationId, file.path);

                const content = typeof data === 'string' ? data : JSON.stringify(data, null, 2);

                if (content.length > MAX_PREVIEW_SIZE) {
                    this.previewContent = content.slice(0, MAX_PREVIEW_SIZE);
                    this.previewTruncated = true;
                } else {
                    this.previewContent = content;
                }
            } catch {
                this.previewError = 'Failed to load file contents. Try downloading the file instead.';
            } finally {
                this.previewLoading = false;
            }
        },
        async handleDownload (file) {
            this.downloadingFiles = { ...this.downloadingFiles, [file.path]: true };
            try {
                const { data } = await fetchClaudeAgentFile(this.conversationId, file.path);

                const content = typeof data === 'string' ? data : JSON.stringify(data, null, 2);

                const blob = new Blob([content], { type: 'application/octet-stream' });

                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.name;
                link.click();
                URL.revokeObjectURL(url);
            } catch {
                // noop
            }

            // Hold spinner briefly after link.click() to bridge the gap
            // before the native save dialog appears
            await new Promise((resolve) => setTimeout(resolve, 2000));
            this.downloadingFiles = { ...this.downloadingFiles, [file.path]: false };
        },
    },
};
</script>

<style scoped lang="less">
@import "@/common/marked/markdown-styles";

.session-files-artifact {
    height: 100%;
    width: 100%;
    overflow-y: auto;
    background: var(--palette-gray-0);
    display: flex;
    flex-direction: column;

    &.inline {
        max-height: 400px;
    }

    &__list {
        display: flex;
        flex-direction: column;
        padding: 8px 0;
    }

    &__file {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;
        cursor: pointer;
        border-bottom: 1px solid var(--palette-gray-20);

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background-color: var(--palette-gray-10);
        }
    }

    &__file-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    &__file-name {
        font-weight: 600;
        font-size: 13px;
        line-height: 1.4;
        color: var(--palette-gray-100);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__file-size {
        font-size: 11px;
        color: var(--palette-gray-60);
        line-height: 1.4;
    }

    &__file-action {
        color: var(--palette-gray-50);
        flex-shrink: 0;
    }

    &__empty {
        padding: 24px 16px;
        text-align: center;
        color: var(--palette-gray-60);
        font-size: 13px;
    }

    &__preview {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }

    &__preview-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        border-bottom: 1px solid var(--palette-gray-20);
        background-color: var(--palette-gray-5);
        position: sticky;
        top: 0;
        z-index: 1;
        box-shadow: 0 2px 4px var(--semantic-drop-shadow-base);
    }

    &__back-button {
        display: flex;
        align-items: center;
        gap: 4px;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 12px;
        color: var(--palette-teal-80);
        padding: 4px 8px;
        border-radius: 4px;
        white-space: nowrap;
        flex-shrink: 0;

        &:hover {
            background-color: var(--palette-gray-20);
        }
    }

    &__preview-filename {
        flex: 1;
        font-weight: 600;
        font-size: 13px;
        color: var(--palette-gray-90);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__render-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;

        span {
            font-size: 11px;
            color: var(--palette-gray-50);
            user-select: none;

            &.active {
                color: var(--palette-gray-90);
                font-weight: 600;
            }
        }
    }

    &__preview-render {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .inline & {
            min-height: 350px;
        }
    }

    &__html-iframe {
        width: 100%;
        flex: 1;
        min-height: 0;
        border: none;
        background: var(--palette-white);
    }

    &__download-button {
        display: flex;
        align-items: center;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--palette-gray-60);
        padding: 4px;
        border-radius: 4px;

        &:hover {
            background-color: var(--palette-gray-20);
            color: var(--palette-gray-90);
        }
    }

    &__preview-loading,
    &__preview-error {
        padding: 24px 16px;
        text-align: center;
        color: var(--palette-gray-60);
        font-size: 13px;
    }

    &__preview-error {
        color: var(--palette-red-80);
    }

    &__preview-content {
        flex: 1;
        overflow: auto;
        padding: 16px;

        pre {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
            font-size: 12px;
            line-height: 1.5;
            color: var(--palette-gray-100);
        }

        code {
            font-family: 'SF Mono', 'Monaco', 'Menlo', 'Courier New', monospace;
        }
    }

    &__truncated-notice {
        margin-top: 12px;
        padding: 8px 12px;
        background-color: var(--palette-yellow-10);
        border: 1px solid var(--palette-yellow-40);
        border-radius: 4px;
        font-size: 12px;
        color: var(--palette-gray-80);

        button {
            background: none;
            border: none;
            cursor: pointer;
            color: var(--palette-teal-80);
            font-weight: 600;
            text-decoration: underline;
            padding: 0;
            font-size: 12px;
        }
    }
}

:deep(.rendered-markdown) {
    .markdown-document-styles();
}
</style>
